import { getStroke } from 'perfect-freehand'

import {
  getCanvasCoord,
  getScaledPointsWithAspectRatio,
  getScaledPointsWithoutAspectRatio,
  getStrokeRegeneration,
  subtractCoordinates,
} from '../../utils/utils.js'

export const handleSelectMove = (state) => {
  const {
    isDragging,
    tmpSelectionRectPathKeys,
    selectionRectPathKeys,
    draggingCurrentPos,
    draggingStartPos,
    clientCoord,
    scope,
    translate,
    draggingRect,
    paths,
    setSelectionRectPathKeys,
    isSelectedMovingActive,
    selectCoordinateAtPointerDown,
    pathsAtPointerDown,
    selectionRect,
    tmpSelectionRect,
    setSelectionRect,
    isResizeActive,
    resizeDirection,
    isSimulatePressure,
    isCapTrue,
    // 동시편집
    ydocRef,
    triggerUpdatePath,
  } = state

  // ============================================================================
  // [1] 선택 박스를 새로 그리고 있는 중 (드래그 박스)
  // ============================================================================
  if (isDragging) {
    tmpSelectionRectPathKeys.current = [...selectionRectPathKeys]

    draggingCurrentPos.current = getCanvasCoord(clientCoord, translate, scope)

    // --- 드래그 박스 영역 계산 ---
    draggingRect.current.top = Math.min(draggingCurrentPos.current.y, draggingStartPos.current.y)
    draggingRect.current.left = Math.min(draggingCurrentPos.current.x, draggingStartPos.current.x)
    draggingRect.current.right = Math.max(draggingCurrentPos.current.x, draggingStartPos.current.x)
    draggingRect.current.bottom = Math.max(draggingCurrentPos.current.y, draggingStartPos.current.y)
    draggingRect.current.width = Math.abs(draggingCurrentPos.current.x - draggingStartPos.current.x)
    draggingRect.current.height = Math.abs(
      draggingCurrentPos.current.y - draggingStartPos.current.y
    )

    // --- 드래그 박스에 포함된 path를 선별 ---
    for (let pathKey of Object.keys(paths)) {
      // isLocked: true; 이미지는 select 불가
      if (paths[pathKey].isLocked === true) continue

      let flag = false
      for (let point of paths[pathKey].points) {
        // dragging box 영역에 포함되는 경우
        if (
          point.x + paths[pathKey].x >= draggingRect.current.left &&
          point.x + paths[pathKey].x <= draggingRect.current.right &&
          point.y + paths[pathKey].y >= draggingRect.current.top &&
          point.y + paths[pathKey].y <= draggingRect.current.bottom
        ) {
          flag = true
          if (!selectionRectPathKeys.includes(pathKey)) {
            // Lee: stop abusing state
            tmpSelectionRectPathKeys.current.push(pathKey)
            break
          }
        }
      }
      // 드래그 박스에서 벗어난 path는 선택 해제
      if (!flag && selectionRectPathKeys.includes(pathKey)) {
        tmpSelectionRectPathKeys.current = tmpSelectionRectPathKeys.current.filter(
          (val) => val !== pathKey
        )
      }
    }

    // 선택된 path 키 목록 갱신
    setSelectionRectPathKeys(tmpSelectionRectPathKeys.current)
  }

  // ============================================================================
  // [2] 선택된 path들을 이동 중
  // ============================================================================
  else if (isSelectedMovingActive) {
    if (selectionRectPathKeys.length === 0) return

    // 이동량 계산 (현재 좌표 - 클릭 시점의 좌표)
    let delta = subtractCoordinates(
      getCanvasCoord(clientCoord, translate, scope),
      selectCoordinateAtPointerDown.current
    )

    // 너무 미세한 이동은 무시
    if (delta.x >= -20 && delta.x < 20 && delta.y >= -20 && delta.y < 20) {
      return
    }

    // --- 각 path 위치 갱신 ---
    // ⭐️ 250930 이우주: move가 아닌 up에서 path 이동 동기화 임시 처리
    // ydocRef.current.transact(() => {
    for (let pathKey of selectionRectPathKeys) {
      paths[pathKey].x = pathsAtPointerDown.current[pathKey].x + delta.x
      paths[pathKey].y = pathsAtPointerDown.current[pathKey].y + delta.y

      // triggerUpdatePath(
      //   pathKey,
      //   {
      //     x: paths[pathKey].x,
      //     y: paths[pathKey].y,
      //   },
      //   'no-op'
      // )
    }
    // })

    // --- 선택 박스 위치 갱신 ---
    selectionRect.top = tmpSelectionRect.current.top + delta.y
    selectionRect.left = tmpSelectionRect.current.left + delta.x
    setSelectionRect({ ...selectionRect })
  }

  // ============================================================================
  // [3] 선택된 path들을 리사이즈 중
  // ============================================================================
  else if (isResizeActive) {
    // 선택 박스 초기화
    setSelectionRect({ top: 0, left: 0, width: 0, height: 0 })

    let delta = subtractCoordinates(
      getCanvasCoord(clientCoord, translate, scope),
      selectCoordinateAtPointerDown.current
    )
    let horizontalDirection
    let verticalDirection
    let isAspectRatioPreserved

    // --- 리사이즈 방향 설정 ---
    if (resizeDirection.current === 'selection-border-right') {
      horizontalDirection = 'R'
      verticalDirection = 'N'
    } else if (resizeDirection.current === 'selection-border-left') {
      horizontalDirection = 'L'
      verticalDirection = 'N'
    } else if (resizeDirection.current === 'selection-border-top') {
      horizontalDirection = 'N'
      verticalDirection = 'T'
    } else if (resizeDirection.current === 'selection-border-bottom') {
      horizontalDirection = 'N'
      verticalDirection = 'B'
    } else if (resizeDirection.current === 'selection-corner-top-left') {
      horizontalDirection = 'L'
      verticalDirection = 'T'
    } else if (resizeDirection.current === 'selection-corner-top-right') {
      horizontalDirection = 'R'
      verticalDirection = 'T'
    } else if (resizeDirection.current === 'selection-corner-bottom-left') {
      horizontalDirection = 'L'
      verticalDirection = 'B'
    } else if (resizeDirection.current === 'selection-corner-bottom-right') {
      horizontalDirection = 'R'
      verticalDirection = 'B'
    }

    // [3-1] 테두리(border) 리사이즈 → 비율 유지 X
    if (resizeDirection.current.includes('border')) {
      for (let pathKey of selectionRectPathKeys) {
        let scaledPoints

        scaledPoints = getScaledPointsWithoutAspectRatio(
          horizontalDirection,
          verticalDirection,
          paths[pathKey],
          pathsAtPointerDown.current[pathKey],
          delta.x,
          delta.y,
          tmpSelectionRect.current,
          getCanvasCoord(clientCoord, translate, scope)
        )

        // 재생성된 stroke 경로 및 points 재할당
        if (paths[pathKey].type !== 'image') {
          paths[pathKey].d = getStrokeRegeneration(
            getStroke,
            paths[pathKey].thickness,
            isSimulatePressure,
            isCapTrue,
            pathKey,
            paths,
            scaledPoints
          )
        }
        paths[pathKey].points = scaledPoints

        // updatedPathsRef.current.set(pathKey, paths[pathKey]) // [동시편집 - throttle]
      }
    }

    // [3-2] 코너(corner) 리사이즈 → 비율 유지 여부 판단
    else {
      isAspectRatioPreserved = false
      for (let pathKey of selectionRectPathKeys) {
        if (paths[pathKey].type === 'image') {
          isAspectRatioPreserved = true
          break
        }
      }

      // 이미지가 포함되어 있으면 비율 유지
      if (isAspectRatioPreserved) {
        for (let pathKey of selectionRectPathKeys) {
          let scaledPoints

          scaledPoints = getScaledPointsWithAspectRatio(
            horizontalDirection,
            verticalDirection,
            paths[pathKey],
            pathsAtPointerDown.current[pathKey],
            delta.x,
            delta.y,
            tmpSelectionRect.current,
            getCanvasCoord(clientCoord, translate, scope)
          )

          // 재생성된 stroke 경로 및 points 재할당
          if (paths[pathKey].type !== 'image') {
            paths[pathKey].d = getStrokeRegeneration(
              getStroke,
              paths[pathKey].thickness,
              isSimulatePressure,
              isCapTrue,
              pathKey,
              paths,
              scaledPoints
            )
          }
          paths[pathKey].points = scaledPoints

          // updatedPathsRef.current.set(pathKey, paths[pathKey]) // [동시편집 - throttle]
        }
      }
      // 선만 있는 경우에는 비율 유지 안 함
      else {
        for (let pathKey of selectionRectPathKeys) {
          let scaledPoints

          scaledPoints = getScaledPointsWithoutAspectRatio(
            horizontalDirection,
            verticalDirection,
            paths[pathKey],
            pathsAtPointerDown.current[pathKey],
            delta.x,
            delta.y,
            tmpSelectionRect.current,
            getCanvasCoord(clientCoord, translate, scope)
          )

          // 재생성된 stroke 경로 및 points 재할당
          if (paths[pathKey].type !== 'image') {
            paths[pathKey].d = getStrokeRegeneration(
              getStroke,
              paths[pathKey].thickness,
              isSimulatePressure,
              isCapTrue,
              pathKey,
              paths,
              scaledPoints
            )
          }
          paths[pathKey].points = scaledPoints
        }
      }

      // ( 리사이즈 이벤트는 up 시점에서만 동시편집 반영 )
    }
  }
}
